import { uiText } from "@/lib/i18n/uiText";
import { useSyncExternalStore, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Languages } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/i18n/LanguageProvider";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import {
  clearTranslationFailure,
  getTranslationFailure,
  subscribeTranslationStatus,
} from "@/lib/i18n/translationStatus";

const TRANSLATION_QUERY_KEYS = [
  "localized-content",
  "localized-fields",
  "localized-list",
  "localized-guide",
];

/**
 * Inline, non-blocking notice shown on localized routes (e.g. /fr) when the
 * runtime translation service fails.
 *
 * The page still renders with localized availability messages and a retry.
 */
export function TranslationErrorNotice() {
  const { locale } = useLanguage();
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);

  const failure = useSyncExternalStore(
    subscribeTranslationStatus,
    getTranslationFailure,
    () => null,
  );

  if (locale === DEFAULT_LOCALE || !failure) return null;

  const retry = async () => {
    setRetrying(true);
    clearTranslationFailure();
    try {
      await Promise.all(
        TRANSLATION_QUERY_KEYS.map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      );
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Alert className="my-4" role="status">
      <Languages className="h-4 w-4" />
      <AlertTitle>{uiText("Translation temporarily unavailable")}</AlertTitle>
      <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span>
           {uiText("Statlign translation is temporarily unavailable.")}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={retry}
          disabled={retrying}
          className="shrink-0"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${retrying ? "animate-spin" : ""}`} />
          {retrying ? uiText("Retrying…") : uiText("Retry translation")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
